/**
 * Pediatric billing-code reference data — `BACKLOG.md` M8: age-banded preventive E&M, vaccine-
 * administration counseling-split codes, and the two developmental-screening billing codes tied to
 * the screening tools themselves. **Reference/informational only** — this module is a pure lookup a
 * route can surface as a suggestion; it is not wired into `server/src/rcm/` charge capture (out of
 * scope for this pass, and RCM's own charge-line/coding model is a separate, larger system this
 * module does not attempt to integrate with).
 *
 * **`96110` (developmental screening, with scoring and documentation) and `96127` (brief emotional/
 * behavioral assessment) are given directly by this task's own brief**, not independently
 * re-derived — cite the brief rather than treating them as independently verified against a current
 * CPT manual.
 *
 * **Every other code below is UNREVIEWED**, per this project's own established convention
 * (`dermatology/phototherapy.ts`'s header) for a value that needs independent clinical/coding review
 * before real use: the age-banded preventive-medicine E&M codes (99381–99397) and the vaccine-
 * administration counseling-split codes (90460/90461 vs. 90471–90474) are recalled from general
 * medical-billing knowledge, not verified against a current CPT manual or AAPC/CMS source during this
 * build. Do not use any of this for a real claim until a certified coder has checked it — the same
 * caution `dermatology/destructionCharges.ts`'s own header gives its CPT count-banding table
 * ("a question for a certified coder rather than an assertion").
 */

export interface AgeBandedEmCode {
  readonly cpt: string;
  readonly display: string;
  readonly minAgeDays: number;
  readonly maxAgeDays: number | null;
}

const DAYS_PER_YEAR = 365.25;
function atYears(years: number): number {
  return Math.round(years * DAYS_PER_YEAR);
}

/** UNREVIEWED — see this module's header. Standard CPT preventive-medicine E&M age bands, new vs.
 * established patient. Only the pediatric-relevant bands (under 18) are included; adult bands are
 * out of scope for this module. */
export const NEW_PATIENT_PREVENTIVE_EM_CODES: readonly AgeBandedEmCode[] = [
  {
    cpt: '99381',
    display: 'Preventive E&M, new patient, infant (under 1 year)',
    minAgeDays: 0,
    maxAgeDays: atYears(1) - 1,
  },
  {
    cpt: '99382',
    display: 'Preventive E&M, new patient, early childhood (1–4 years)',
    minAgeDays: atYears(1),
    maxAgeDays: atYears(5) - 1,
  },
  {
    cpt: '99383',
    display: 'Preventive E&M, new patient, late childhood (5–11 years)',
    minAgeDays: atYears(5),
    maxAgeDays: atYears(12) - 1,
  },
  {
    cpt: '99384',
    display: 'Preventive E&M, new patient, adolescent (12–17 years)',
    minAgeDays: atYears(12),
    maxAgeDays: atYears(18) - 1,
  },
];

export const ESTABLISHED_PATIENT_PREVENTIVE_EM_CODES: readonly AgeBandedEmCode[] = [
  {
    cpt: '99391',
    display: 'Preventive E&M, established patient, infant (under 1 year)',
    minAgeDays: 0,
    maxAgeDays: atYears(1) - 1,
  },
  {
    cpt: '99392',
    display: 'Preventive E&M, established patient, early childhood (1–4 years)',
    minAgeDays: atYears(1),
    maxAgeDays: atYears(5) - 1,
  },
  {
    cpt: '99393',
    display: 'Preventive E&M, established patient, late childhood (5–11 years)',
    minAgeDays: atYears(5),
    maxAgeDays: atYears(12) - 1,
  },
  {
    cpt: '99394',
    display: 'Preventive E&M, established patient, adolescent (12–17 years)',
    minAgeDays: atYears(12),
    maxAgeDays: atYears(18) - 1,
  },
];

/** The single age-banded code matching `ageDays`, for `newPatient` vs. established — `null` if
 * `ageDays` falls outside every band (this module's pediatric-only bands stop at 18 years), refusing
 * rather than guessing an adult code it does not carry. */
export function preventiveEmCodeForAge(
  ageDays: number,
  newPatient: boolean,
): AgeBandedEmCode | null {
  const bands = newPatient
    ? NEW_PATIENT_PREVENTIVE_EM_CODES
    : ESTABLISHED_PATIENT_PREVENTIVE_EM_CODES;
  return (
    bands.find(
      (band) =>
        ageDays >= band.minAgeDays && (band.maxAgeDays === null || ageDays <= band.maxAgeDays),
    ) ?? null
  );
}

export interface VaccineAdministrationCodeLine {
  readonly cpt: string;
  readonly display: string;
}

/**
 * UNREVIEWED — see this module's header. The counseling-split ACIP-era vaccine-administration codes:
 * 90460/90461 apply only when a physician or other qualified healthcare professional provides
 * face-to-face vaccine counseling to the patient/guardian, and only through age 18; 90471–90474 apply
 * when no such counseling is furnished (a nurse-administered vaccine with no physician counseling
 * component, e.g.). `withCounseling` selects which pair applies; `componentCount` is doses/components
 * given in the same encounter (a combination vaccine like DTaP-IPV-Hib counts as multiple components).
 * Refuses on a non-positive count rather than guessing, matching `destructionCptCodes`'s convention.
 */
export function vaccineAdministrationCodes(
  componentCount: number,
  withCounseling: boolean,
): readonly VaccineAdministrationCodeLine[] | null {
  if (!Number.isFinite(componentCount) || componentCount <= 0) return null;
  const count = Math.trunc(componentCount);

  if (withCounseling) {
    const lines: VaccineAdministrationCodeLine[] = [
      { cpt: '90460', display: 'Vaccine administration with counseling, first component' },
    ];
    if (count > 1) {
      lines.push({
        cpt: '90461',
        display: `Vaccine administration with counseling, each additional component (×${count - 1})`,
      });
    }
    return lines;
  }

  const lines: VaccineAdministrationCodeLine[] = [
    { cpt: '90471', display: 'Vaccine administration, first vaccine (no counseling)' },
  ];
  if (count > 1) {
    lines.push({
      cpt: '90472',
      display: `Vaccine administration, each additional vaccine, no counseling (×${count - 1})`,
    });
  }
  return lines;
}

/**
 * Developmental-screening billing codes tied to the screening tool used — **given directly by this
 * task's own brief**, per this module's header. `96110` (developmental screening with scoring and
 * documentation) applies to a standardized tool result like M-CHAT-R/F; `96127` (brief emotional/
 * behavioral assessment) is a separate, narrower code for a brief standardized instrument, not
 * specific to autism screening. Both are returned as independent options rather than this module
 * picking one on the caller's behalf — which one applies is a documentation/coding judgment call,
 * not something a screening-tool name alone determines.
 */
export const DEVELOPMENTAL_SCREENING_BILLING_CODES: readonly VaccineAdministrationCodeLine[] = [
  { cpt: '96110', display: 'Developmental screening, with scoring and documentation' },
  { cpt: '96127', display: 'Brief emotional/behavioral assessment' },
];
